<template>
  <footer class="bg-white text-gray-700" role="contentinfo">
    <!-- Top promotional section - Hidden on mobile -->


    <!-- Footer links section -->
    <div class="border-t border-gray-200">
      <div class="max-w-7xl mx-auto px-4 lg:px-8">
        <!-- Desktop Layout -->
        <div class="hidden lg:grid lg:grid-cols-5 lg:gap-8 lg:py-12">
          <!-- Contact Information -->
          <div class="lg:col-span-1">
            <h4 class="font-bold text-gray-900 mb-4 text-lg">{{ $t('footer.customerService') }}</h4>
            <div class="text-gray-600 mb-4 text-base">{{ $t('footer.hongKong') }}: +852 84830276</div>

            <h4 class="font-bold text-gray-900 mb-4 text-lg">{{ $t('footer.customerEmail') }}</h4>
            <div class="text-gray-600 mb-4 text-base">
              <a href="mailto:ops@redlandsec.com" class="hover:text-hongtu-primary transition-colors">ops@redlandsec.com</a>
            </div>

            <h4 class="font-bold text-gray-900 mb-4 text-lg">{{ $t('footer.businessCooperation') }}</h4>
            <div class="text-gray-600 text-base">
              <a href="mailto:info@redlandsec.com" class="hover:text-hongtu-primary transition-colors">info@redlandsec.com</a>
            </div>
          </div>

          <!-- Trading -->
          <div>
            <h4 class="font-bold text-gray-900 mb-4 text-lg">{{ $t('footer.trading') }}</h4>
            <ul class="space-y-3">
              <li>
                <component
                  :is="getLinkComponent('footer_pricing')"
                  v-bind="getLinkProps('footer_pricing')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_pricing', $t('footer.pricing')) }}
                </component>
              </li>
              <li>
                <component
                  :is="getLinkComponent('footer_stock_investment')"
                  v-bind="getLinkProps('footer_stock_investment')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_stock_investment', $t('footer.stockInvestment')) }}
                </component>
              </li>
              <li>
                <component
                  :is="getLinkComponent('footer_fund_investment')"
                  v-bind="getLinkProps('footer_fund_investment')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_fund_investment', $t('footer.fundInvestment')) }}
                </component>
              </li>
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_account_delegate')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_account_delegate')) ? getFooterRoute('footer_account_delegate') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_account_delegate')) ? undefined : getFooterRoute('footer_account_delegate')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_account_delegate', $t('footer.accountDelegate')) }}
                </component>
              </li>
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_insurance_investment')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_insurance_investment')) ? getFooterRoute('footer_insurance_investment') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_insurance_investment')) ? undefined : getFooterRoute('footer_insurance_investment')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_insurance_investment', $t('footer.insuranceInvestment')) }}
                </component>
              </li>
            </ul>
          </div>

          <!-- Customers -->
          <div>
            <h4 class="font-bold text-gray-900 mb-4 text-lg">{{ $t('footer.customers') }}</h4>
            <ul class="space-y-3">
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_stock_customers')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_stock_customers')) ? getFooterRoute('footer_stock_customers') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_stock_customers')) ? undefined : getFooterRoute('footer_stock_customers')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_stock_customers', $t('footer.stockCustomers')) }}
                </component>
              </li>
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_fund_customers')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_fund_customers')) ? getFooterRoute('footer_fund_customers') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_fund_customers')) ? undefined : getFooterRoute('footer_fund_customers')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_fund_customers', $t('footer.fundCustomers')) }}
                </component>
              </li>
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_delegate_customers')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_delegate_customers')) ? getFooterRoute('footer_delegate_customers') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_delegate_customers')) ? undefined : getFooterRoute('footer_delegate_customers')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_delegate_customers', $t('footer.delegateCustomers')) }}
                </component>
              </li>
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_insurance_customers')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_insurance_customers')) ? getFooterRoute('footer_insurance_customers') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_insurance_customers')) ? undefined : getFooterRoute('footer_insurance_customers')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_insurance_customers', $t('footer.insuranceCustomers')) }}
                </component>
              </li>
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_help_center')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_help_center')) ? getFooterRoute('footer_help_center') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_help_center')) ? undefined : getFooterRoute('footer_help_center')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_help_center', $t('footer.helpCenter')) }}
                </component>
              </li>
            </ul>
          </div>

          <!-- About Hongtu -->
          <div>
            <h4 class="font-bold text-gray-900 mb-4 text-lg">{{ $t('footer.aboutHongtu') }}</h4>
            <ul class="space-y-3">
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_about_us')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_about_us')) ? getFooterRoute('footer_about_us') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_about_us')) ? undefined : getFooterRoute('footer_about_us')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_about_us', $t('footer.aboutUs')) }}
                </component>
              </li>
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_company_announcements')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_company_announcements')) ? getFooterRoute('footer_company_announcements') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_company_announcements')) ? undefined : getFooterRoute('footer_company_announcements')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_company_announcements', $t('footer.companyAnnouncements')) }}
                </component>
              </li>
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_media_coverage')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_media_coverage')) ? getFooterRoute('footer_media_coverage') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_media_coverage')) ? undefined : getFooterRoute('footer_media_coverage')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_media_coverage', $t('footer.mediaCoverage')) }}
                </component>
              </li>
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_join_us')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_join_us')) ? getFooterRoute('footer_join_us') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_join_us')) ? undefined : getFooterRoute('footer_join_us')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_join_us', $t('footer.joinUs')) }}
                </component>
              </li>
            </ul>
          </div>

          <!-- Terms -->
          <div>
            <h4 class="font-bold text-gray-900 mb-4 text-lg">{{ $t('footer.terms') }}</h4>
            <ul class="space-y-3">
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_disclaimer')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_disclaimer')) ? getFooterRoute('footer_disclaimer') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_disclaimer')) ? undefined : getFooterRoute('footer_disclaimer')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_disclaimer', $t('footer.disclaimer')) }}
                </component>
              </li>
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_terms_of_service')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_terms_of_service')) ? getFooterRoute('footer_terms_of_service') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_terms_of_service')) ? undefined : getFooterRoute('footer_terms_of_service')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_terms_of_service', $t('footer.termsOfService')) }}
                </component>
              </li>
              <li>
                <component
                  :is="isRouterLink(getFooterRoute('footer_privacy_policy')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_privacy_policy')) ? getFooterRoute('footer_privacy_policy') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_privacy_policy')) ? undefined : getFooterRoute('footer_privacy_policy')"
                  class="text-base text-gray-600 hover:text-hongtu-primary transition-colors block"
                >
                  {{ getFooterText('footer_privacy_policy', $t('footer.privacyPolicy')) }}
                </component>
              </li>
            </ul>
          </div>
        </div>

        <!-- Mobile Layout - Accordion Style -->
        <div class="lg:hidden py-6">
          <div class="space-y-4">
            <!-- Contact Section -->
            <div class="border-b border-gray-200 pb-4">
              <button
                @click="toggleSection('contact')"
                class="footer-accordion-btn hover:bg-gray-50 rounded-lg px-3 -mx-3"
              >
                <h4 class="font-bold text-gray-900 text-lg">{{ $t('footer.contactUs') }}</h4>
                <svg
                  :class="['w-5 h-5 transition-transform duration-200 text-gray-500', expandedSections.contact ? 'rotate-180' : '']"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                :class="['footer-accordion-content', expandedSections.contact ? 'expanded' : 'collapsed']"
                class="mt-3 space-y-3 pl-4 border-l-2 border-hongtu-primary/20"
              >
                <div>
                  <div class="font-medium text-gray-900 mb-1">{{ $t('footer.customerService') }}</div>
                  <div class="text-gray-600">{{ $t('footer.hongKong') }}: +852 84830276</div>
                </div>
                <div>
                  <div class="font-medium text-gray-900 mb-1">{{ $t('footer.customerEmail') }}</div>
                  <a href="mailto:ops@redlandsec.com" class="text-hongtu-primary hover:text-hongtu-secondary transition-colors">ops@redlandsec.com</a>
                </div>
                <div>
                  <div class="font-medium text-gray-900 mb-1">{{ $t('footer.businessCooperation') }}</div>
                  <a href="mailto:info@redlandsec.com" class="text-hongtu-primary hover:text-hongtu-secondary transition-colors">info@redlandsec.com</a>
                </div>
              </div>
            </div>

            <!-- Services Section -->
            <div class="border-b border-gray-200 pb-4">
              <button
                @click="toggleSection('services')"
                class="footer-accordion-btn hover:bg-gray-50 rounded-lg px-3 -mx-3"
              >
                <h4 class="font-bold text-gray-900 text-lg">{{ $t('footer.servicesProducts') }}</h4>
                <svg
                  :class="['w-5 h-5 transition-transform duration-200 text-gray-500', expandedSections.services ? 'rotate-180' : '']"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                :class="['footer-accordion-content', expandedSections.services ? 'expanded' : 'collapsed']"
                class="mt-3 grid grid-cols-2 gap-4 pl-4 border-l-2 border-hongtu-primary/20"
              >
                <div>
                  <h5 class="font-medium text-gray-900 mb-2">{{ $t('footer.trading') }}</h5>
                  <ul class="space-y-1">
                    <li>
                      <component
                        :is="isRouterLink(getFooterRoute('footer_stock_investment')) ? 'router-link' : 'a'"
                        :to="isRouterLink(getFooterRoute('footer_stock_investment')) ? getFooterRoute('footer_stock_investment') : undefined"
                        :href="isRouterLink(getFooterRoute('footer_stock_investment')) ? undefined : getFooterRoute('footer_stock_investment')"
                        class="text-sm text-gray-600 hover:text-hongtu-primary transition-colors block"
                      >
                        {{ getFooterText('footer_stock_investment', $t('footer.stockInvestment')) }}
                      </component>
                    </li>
                    <li>
                      <component
                        :is="isRouterLink(getFooterRoute('footer_fund_investment')) ? 'router-link' : 'a'"
                        :to="isRouterLink(getFooterRoute('footer_fund_investment')) ? getFooterRoute('footer_fund_investment') : undefined"
                        :href="isRouterLink(getFooterRoute('footer_fund_investment')) ? undefined : getFooterRoute('footer_fund_investment')"
                        class="text-sm text-gray-600 hover:text-hongtu-primary transition-colors block"
                      >
                        {{ getFooterText('footer_fund_investment', $t('footer.fundInvestment')) }}
                      </component>
                    </li>
                    <li>
                      <component
                        :is="isRouterLink(getFooterRoute('footer_account_delegate')) ? 'router-link' : 'a'"
                        :to="isRouterLink(getFooterRoute('footer_account_delegate')) ? getFooterRoute('footer_account_delegate') : undefined"
                        :href="isRouterLink(getFooterRoute('footer_account_delegate')) ? undefined : getFooterRoute('footer_account_delegate')"
                        class="text-sm text-gray-600 hover:text-hongtu-primary transition-colors block"
                      >
                        {{ getFooterText('footer_account_delegate', $t('footer.accountDelegate')) }}
                      </component>
                    </li>
                    <li>
                      <component
                        :is="isRouterLink(getFooterRoute('footer_insurance_investment')) ? 'router-link' : 'a'"
                        :to="isRouterLink(getFooterRoute('footer_insurance_investment')) ? getFooterRoute('footer_insurance_investment') : undefined"
                        :href="isRouterLink(getFooterRoute('footer_insurance_investment')) ? undefined : getFooterRoute('footer_insurance_investment')"
                        class="text-sm text-gray-600 hover:text-hongtu-primary transition-colors block"
                      >
                        {{ getFooterText('footer_insurance_investment', $t('footer.insuranceInvestment')) }}
                      </component>
                    </li>
                  </ul>
                </div>
                <div>
                  <h5 class="font-medium text-gray-900 mb-2">{{ $t('footer.customerServiceSection') }}</h5>
                  <ul class="space-y-1">
                    <li>
                      <component
                        :is="isRouterLink(getFooterRoute('footer_help_center_mobile')) ? 'router-link' : 'a'"
                        :to="isRouterLink(getFooterRoute('footer_help_center_mobile')) ? getFooterRoute('footer_help_center_mobile') : undefined"
                        :href="isRouterLink(getFooterRoute('footer_help_center_mobile')) ? undefined : getFooterRoute('footer_help_center_mobile')"
                        class="text-sm text-gray-600 hover:text-hongtu-primary transition-colors block"
                      >
                        {{ getFooterText('footer_help_center_mobile', $t('footer.helpCenter')) }}
                      </component>
                    </li>
                    <li>
                      <component
                        :is="isRouterLink(getFooterRoute('footer_pricing_standards')) ? 'router-link' : 'a'"
                        :to="isRouterLink(getFooterRoute('footer_pricing_standards')) ? getFooterRoute('footer_pricing_standards') : undefined"
                        :href="isRouterLink(getFooterRoute('footer_pricing_standards')) ? undefined : getFooterRoute('footer_pricing_standards')"
                        class="text-sm text-gray-600 hover:text-hongtu-primary transition-colors block"
                      >
                        {{ getFooterText('footer_pricing_standards', $t('footer.pricingStandards')) }}
                      </component>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Company Section -->
            <div class="border-b border-gray-200 pb-4">
              <button
                @click="toggleSection('company')"
                class="footer-accordion-btn hover:bg-gray-50 rounded-lg px-3 -mx-3"
              >
                <h4 class="font-bold text-gray-900 text-lg">{{ $t('footer.aboutHongtu') }}</h4>
                <svg
                  :class="['w-5 h-5 transition-transform duration-200 text-gray-500', expandedSections.company ? 'rotate-180' : '']"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                :class="['footer-accordion-content', expandedSections.company ? 'expanded' : 'collapsed']"
                class="mt-3 grid grid-cols-2 gap-4 pl-4 border-l-2 border-hongtu-primary/20"
              >
                <div>
                  <h5 class="font-medium text-gray-900 mb-2">{{ $t('footer.companyInfo') }}</h5>
                  <ul class="space-y-1">
                    <li>
                      <component
                        :is="isRouterLink(getFooterRoute('footer_about_us')) ? 'router-link' : 'a'"
                        :to="isRouterLink(getFooterRoute('footer_about_us')) ? getFooterRoute('footer_about_us') : undefined"
                        :href="isRouterLink(getFooterRoute('footer_about_us')) ? undefined : getFooterRoute('footer_about_us')"
                        class="text-sm text-gray-600 hover:text-hongtu-primary transition-colors block"
                      >
                        {{ getFooterText('footer_about_us', $t('footer.aboutUs')) }}
                      </component>
                    </li>
                    <li>
                      <component
                        :is="isRouterLink(getFooterRoute('footer_company_announcements')) ? 'router-link' : 'a'"
                        :to="isRouterLink(getFooterRoute('footer_company_announcements')) ? getFooterRoute('footer_company_announcements') : undefined"
                        :href="isRouterLink(getFooterRoute('footer_company_announcements')) ? undefined : getFooterRoute('footer_company_announcements')"
                        class="text-sm text-gray-600 hover:text-hongtu-primary transition-colors block"
                      >
                        {{ getFooterText('footer_company_announcements', $t('footer.companyAnnouncements')) }}
                      </component>
                    </li>
                  </ul>
                </div>
                <div>
                  <h5 class="font-medium text-gray-900 mb-2">{{ $t('footer.more') }}</h5>
                  <ul class="space-y-1">
                    <li>
                      <component
                        :is="isRouterLink(getFooterRoute('footer_media_coverage')) ? 'router-link' : 'a'"
                        :to="isRouterLink(getFooterRoute('footer_media_coverage')) ? getFooterRoute('footer_media_coverage') : undefined"
                        :href="isRouterLink(getFooterRoute('footer_media_coverage')) ? undefined : getFooterRoute('footer_media_coverage')"
                        class="text-sm text-gray-600 hover:text-hongtu-primary transition-colors block"
                      >
                        {{ getFooterText('footer_media_coverage', $t('footer.mediaCoverage')) }}
                      </component>
                    </li>
                    <li>
                      <component
                        :is="isRouterLink(getFooterRoute('footer_join_us')) ? 'router-link' : 'a'"
                        :to="isRouterLink(getFooterRoute('footer_join_us')) ? getFooterRoute('footer_join_us') : undefined"
                        :href="isRouterLink(getFooterRoute('footer_join_us')) ? undefined : getFooterRoute('footer_join_us')"
                        class="text-sm text-gray-600 hover:text-hongtu-primary transition-colors block"
                      >
                        {{ getFooterText('footer_join_us', $t('footer.joinUs')) }}
                      </component>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Legal Section -->
            <div>
              <button
                @click="toggleSection('legal')"
                class="footer-accordion-btn hover:bg-gray-50 rounded-lg px-3 -mx-3"
              >
                <h4 class="font-bold text-gray-900 text-lg">{{ $t('footer.legalTerms') }}</h4>
                <svg
                  :class="['w-5 h-5 transition-transform duration-200 text-gray-500', expandedSections.legal ? 'rotate-180' : '']"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                :class="['footer-accordion-content', expandedSections.legal ? 'expanded' : 'collapsed']"
                class="mt-3 space-y-2 pl-4 border-l-2 border-hongtu-primary/20"
              >
                <component
                  :is="isRouterLink(getFooterRoute('footer_disclaimer')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_disclaimer')) ? getFooterRoute('footer_disclaimer') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_disclaimer')) ? undefined : getFooterRoute('footer_disclaimer')"
                  class="block text-sm text-gray-600 hover:text-hongtu-primary transition-colors"
                >
                  {{ getFooterText('footer_disclaimer', $t('footer.disclaimer')) }}
                </component>
                <component
                  :is="isRouterLink(getFooterRoute('footer_terms_of_service')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_terms_of_service')) ? getFooterRoute('footer_terms_of_service') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_terms_of_service')) ? undefined : getFooterRoute('footer_terms_of_service')"
                  class="block text-sm text-gray-600 hover:text-hongtu-primary transition-colors"
                >
                  {{ getFooterText('footer_terms_of_service', $t('footer.termsOfService')) }}
                </component>
                <component
                  :is="isRouterLink(getFooterRoute('footer_privacy_policy')) ? 'router-link' : 'a'"
                  :to="isRouterLink(getFooterRoute('footer_privacy_policy')) ? getFooterRoute('footer_privacy_policy') : undefined"
                  :href="isRouterLink(getFooterRoute('footer_privacy_policy')) ? undefined : getFooterRoute('footer_privacy_policy')"
                  class="block text-sm text-gray-600 hover:text-hongtu-primary transition-colors"
                >
                  {{ getFooterText('footer_privacy_policy', $t('footer.privacyPolicy')) }}
                </component>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Disclaimer -->
    <div class="border-t border-gray-200 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
        <p class="text-sm lg:text-base text-gray-600 leading-relaxed max-w-none">
          {{ getFooterText('footer_disclaimer_text', $t('footer.disclaimerText')) }}
        </p>
      </div>
    </div>
  </footer>
</template>

<script>
import { ContentMixin, getContentsByCategory } from '../services/contentService'

export default {
  name: 'SiteFooter',
  mixins: [ContentMixin],
  data() {
    return {
      expandedSections: {
        contact: false,
        services: false,
        company: false,
        legal: false
      },
      footerRoutes: {}
    }
  },
  computed: {
    currentLang() {
      return this.$i18n ? this.$i18n.locale : 'zh'
    }
  },
  async created() {
    // 监听语言切换事件
    window.addEventListener('localeChange', this.handleLocaleChange)

    // 加载页脚配置
    await this.loadFooterContent()
  },
  beforeDestroy() {
    window.removeEventListener('localeChange', this.handleLocaleChange)
  },
  methods: {
    async loadFooterContent() {
      try {
        // 获取页脚配置（包含文本和路由）
        const footerData = await getContentsByCategory('footer', this.currentLang)

        // 处理数据：保持原始结构，route类型为对象，text类型为字符串
        this.contentData = { ...this.contentData, ...footerData }
      } catch (error) {
        console.error('加载页脚配置失败:', error)
      }
    },

    handleLocaleChange() {
      this.loadFooterContent()
    },

    getFooterRoute(key, defaultValue = '#') {
      const item = this.contentData[key]
      if (item && typeof item === 'object' && item.route) {
        return item.route
      }
      return defaultValue
    },

    getFooterRouteType(key) {
      const item = this.contentData[key]
      if (item && typeof item === 'object' && item.route_type) {
        return item.route_type
      }
      return 'external'
    },

    getFooterText(key, defaultValue = '') {
      const item = this.contentData[key]
      if (item && typeof item === 'object' && item.text) {
        return item.text
      }
      if (typeof item === 'string') {
        return item
      }
      return defaultValue
    },

    getFooterLinkProps(key) {
      const route = this.getFooterRoute(key)
      const routeType = this.getFooterRouteType(key)
      return {
        is: routeType === 'internal' ? 'router-link' : 'a',
        to: routeType === 'internal' ? route : undefined,
        href: routeType === 'internal' ? undefined : route
      }
    },

    // 获取链接组件类型
    getLinkComponent(key) {
      const routeType = this.getFooterRouteType(key)
      return routeType === 'internal' ? 'router-link' : 'a'
    },

    // 获取链接属性
    getLinkProps(key) {
      const route = this.getFooterRoute(key)
      const routeType = this.getFooterRouteType(key)

      if (routeType === 'internal') {
        return { to: route }
      } else {
        // 确保外部链接包含协议
        let href = route
        if (href && !href.startsWith('http://') && !href.startsWith('https://')) {
          // 如果是纯域名，添加https协议
          if (href.includes('.') && !href.includes('/')) {
            href = 'https://' + href
          }
        }
        return { href, target: '_blank', rel: 'noopener noreferrer' }
      }
    },

    isRouterLink(route) {
      return route && route.startsWith('/')
    },

    toggleSection(section) {
      // 关闭其他所有部分，只展开当前点击的部分
      Object.keys(this.expandedSections).forEach(key => {
        this.expandedSections[key] = key === section ? !this.expandedSections[key] : false
      })
    }
  }
}
</script>


